import { ReusableModal } from '../modals/ReusableModal';
import { numberFormatter } from '../helpers/formatters';
import { useDebouncedCallback } from '../helpers/hooks';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { PointBadge } from '../helpers/PointBadge';
import { selectAllPoints } from '../map/slice/mapSelectors';
import { Contract } from '@/types';
import { EditIcon, EyeIcon, EyeOffIcon, FilterIcon } from '@iconicicons/react';
import React, { SVGProps, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '../helpers/IconButton';
import { useLocation } from 'react-router-dom';
import {
  useLazyGetContractQuery,
  useCreateUpdateContractMutation,
  useGetZonesQuery,
} from '../services/edgeApi';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { toZoneName } from '../utils/stringUtils';
import { TextDivider } from '../helpers/TextDivider';

export type ContractTableFilters = {
  k_id: string;
  owner: string;
  tsp_name: string;
  receipt_points: string;
  delivery_points: string;
  type: string;
  max_daily_quantity: [number | undefined, number | undefined];
};

export type ContractTableProps = {
  contracts: Contract[];
  onChangeFilters?: (filters: ContractTableFilters) => void;
  visibleContracts: { [contractId: string]: Contract };
  setVisibility: (contracts: Contract[], isVisible: boolean) => void;
  isLoading?: boolean;
  showFilters?: boolean;
  isEmpty?: boolean;
  isEditable?: boolean;
  editIcon?: React.FC<SVGProps<SVGSVGElement>>;
  onEditContract?: (contract: Contract) => void;
  showUseInScenarios?: boolean;
};

export const ContractTable: React.FC<ContractTableProps> = ({
  contracts,
  visibleContracts,
  setVisibility,
  isLoading = false,
  onChangeFilters,
  showFilters = true,
  isEmpty = contracts.length === 0,
  isEditable = false,
  editIcon = EditIcon,
  onEditContract = () => {},
  showUseInScenarios = true,
}) => {
  const { data: zones } = useGetZonesQuery(undefined);
  const [getContractTrigger] = useLazyGetContractQuery();
  const [updateContract] = useCreateUpdateContractMutation();
  const points = useSelector(selectAllPoints);
  const [rawData, setRawData] = useState<Object>({});
  const [filters, setFilters] = useState<ContractTableFilters>({
    k_id: '',
    owner: '',
    tsp_name: '',
    receipt_points: '',
    delivery_points: '',
    type: '',
    max_daily_quantity: [undefined, undefined],
  });

  // Track optimistic updates for use_in_scenarios checkboxes
  const [useInScenariosOverrides, setUseInScenariosOverrides] = useState<
    Record<string | number, boolean>
  >({});

  const location = useLocation();
  const locationContract = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const contractId = params.get('edit_contract');
    return contractId;
  }, [location.search]);

  // Sync optimistic overrides with actual contract data when contracts update
  useEffect(() => {
    setUseInScenariosOverrides((prev) => {
      const next = { ...prev };
      let changed = false;

      // Remove overrides that now match the actual contract value
      for (const contractId in next) {
        const contract = contracts.find(
          (c) => String(c.id) === String(contractId),
        );
        if (contract && contract.use_in_scenarios === next[contractId]) {
          delete next[contractId];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [contracts]);

  useEffect(() => {
    if (locationContract && isEditable) {
      getContractTrigger(Number(locationContract))
        .unwrap()
        .then((result) => {
          onEditContract?.(result);
        })
        .catch((error) => {
          console.error('Error fetching contract for editing:', error);
        });
    }
  }, [locationContract]);

  useEffect(() => {
    onChangeFilters?.(filters);
  }, [filters, onChangeFilters]);

  const handleFilterChange = useDebouncedCallback((key: string, value: any) => {
    if (key.match(/^max_daily_quantity\[(0|1)\]$/)) {
      const index = parseInt(
        key.match(/^max_daily_quantity\[(0|1)\]$/)![1],
        10,
      );
      setFilters((current) => {
        const mdq = current.max_daily_quantity || [undefined, undefined];
        (mdq as [number | undefined, number | undefined])[index] = value
          ? Number(value)
          : undefined;
        return { ...current, max_daily_quantity: mdq };
      });
    } else {
      setFilters((current) => ({ ...current, [key]: value }));
    }
  });

  const filterStyle = showFilters
    ? { display: 'flex' }
    : { display: 'none', width: 0, height: 0 };

  const headers = [
    <th key="index" className="text-center">
      {contracts.some((contract) => visibleContracts[contract.id]) ? (
        <EyeIcon
          role="button"
          onClick={() => setVisibility(contracts, false)}
        />
      ) : (
        <EyeOffIcon
          role="button"
          onClick={() => setVisibility(contracts, true)}
        />
      )}
      <div className="mt-2 text-center" style={filterStyle}>
        <FilterIcon className="mx-3" />
      </div>
    </th>,
    ...(showUseInScenarios
      ? [
          <th
            key={'use_in_scenarios'}
            className="text-center"
            title="Use in Scenarios"
          >
            <span
              style={{ fontSize: '1.5em', cursor: 'help' }}
              title="Use in Scenarios"
            >
              →
            </span>
          </th>,
        ]
      : []),
    <th key={'k_id'}>
      Contract Number
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) => handleFilterChange('k_id', evt.target.value)}
        />
      </div>
    </th>,
    <th key={'description'}>
      Name
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) =>
            handleFilterChange('description', evt.target.value)
          }
        />
      </div>
    </th>,
    <th key={'owner'}>
      Owner
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) => handleFilterChange('owner', evt.target.value)}
        />
      </div>
    </th>,
    <th key={'tsp_name'}>
      TSP Name
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) => handleFilterChange('tsp_name', evt.target.value)}
        />
      </div>
    </th>,
    <th key={'receipt_zones'}>Receipts</th>,
    <th key={'delivery_points'}>Deliveries</th>,
    <th
      key={'type'}
      style={{
        width: 102,
      }}
    >
      Type
      <div className="align-items-center mt-2" style={filterStyle}>
        <select
          className="form-select"
          defaultValue=""
          onChange={(evt) => handleFilterChange('type', evt.target.value)}
        >
          <option value=""> </option>
          <option value="IT">IT</option>
          <option value="FIRM">FIRM</option>
        </select>
      </div>
    </th>,
    <th
      key={'fuel_rate_schedule_name'}
      style={{
        width: 102,
      }}
    >
      Fuel Rate
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) =>
            handleFilterChange('fuel_rate_schedule_name', evt.target.value)
          }
        />
      </div>
    </th>,
    <th
      key={'commodity_rate_schedule_name'}
      style={{
        width: 102,
      }}
    >
      Commodity Rate
      <div className="align-items-center mt-2" style={filterStyle}>
        <input
          className="form-control"
          type="text"
          onChange={(evt) =>
            handleFilterChange('commodity_rate_schedule_name', evt.target.value)
          }
        />
      </div>
    </th>,
    <th
      key={'max_daily_quantity'}
      style={
        showFilters
          ? {
              minWidth: 220,
              maxWidth: 350,
            }
          : {}
      }
    >
      MDQ
      <div className="align-items-center mt-2 input-group" style={filterStyle}>
        <input
          className="form-control"
          type="number"
          placeholder="Min"
          onChange={(evt) =>
            handleFilterChange('max_daily_quantity[0]', evt.target.value)
          }
        />
        <input
          className="form-control"
          type="number"
          placeholder="Max"
          onChange={(evt) =>
            handleFilterChange('max_daily_quantity[1]', evt.target.value)
          }
        />
      </div>
    </th>,
    <th key={'start_date'}>Contract Start</th>,
    <th key={'end_date'}>Contract End</th>,
  ];

  return (
    <>
      <table className="table">
        <thead
          style={{
            position: 'sticky',
            top: 0,
          }}
        >
          <tr>{headers}</tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={headers.length} className="text-center">
                <LoadingIndicator />
              </td>
            </tr>
          )}
          {isEmpty ? (
            <tr>
              <td colSpan={headers.length} className="text-center">
                No records found
              </td>
            </tr>
          ) : (
            contracts.map((contract) => {
              return (
                <tr key={contract.id.toString()}>
                  <td className="text-center">
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      {visibleContracts[contract.id] ? (
                        <EyeIcon
                          role="button"
                          onClick={() => setVisibility([contract], false)}
                        />
                      ) : (
                        <EyeOffIcon
                          role="button"
                          onClick={() => setVisibility([contract], true)}
                        />
                      )}
                      {isEditable && (
                        <IconButton
                          // className="ms-3"
                          icon={editIcon}
                          onClick={() => onEditContract(contract)}
                        />
                      )}
                    </div>
                  </td>
                  {showUseInScenarios && (
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        title="Use in Scenarios"
                        checked={
                          contract.id in useInScenariosOverrides
                            ? useInScenariosOverrides[contract.id]
                            : !!contract.use_in_scenarios
                        }
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          // Optimistic update
                          setUseInScenariosOverrides((prev) => ({
                            ...prev,
                            [contract.id]: newValue,
                          }));
                          try {
                            await updateContract({
                              id: contract.id,
                              use_in_scenarios: newValue,
                            }).unwrap();
                            // Success - keep the optimistic update
                          } catch (err) {
                            console.error('Error updating contract:', err);
                            // Revert optimistic update on error
                            setUseInScenariosOverrides((prev) => {
                              const next = { ...prev };
                              delete next[contract.id];
                              return next;
                            });
                          }
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <span onDoubleClick={() => setRawData(contract)}>
                      {contract.k_id}
                    </span>
                  </td>
                  <td
                    style={{
                      maxWidth: 500,
                    }}
                  >
                    <span>{contract.name}</span>
                  </td>
                  <td>{contract.organization_name ?? contract.owner}</td>
                  <td>{contract.tsp_name}</td>
                  <td>
                    {contract.delivery_zones?.length > 0 && (
                      <TextDivider>Zones</TextDivider>
                    )}
                    {contract.receipt_zones
                      .map((zoneId) => zones?.[zoneId]?.name)
                      .filter(Boolean)
                      .map((zoneName) => toZoneName(zoneName!))
                      .sort((a, b) =>
                        a.localeCompare(b, undefined, {
                          numeric: true,
                          sensitivity: 'base',
                        }),
                      )
                      .map((zoneName) => (
                        <BadgeDisplay
                          key={`${contract.id}-receipt-zone-${zoneName}`}
                        >
                          {zoneName}
                        </BadgeDisplay>
                      ))}
                    {contract.receipt_volumes.length > 0 && (
                      <TextDivider>Points</TextDivider>
                    )}
                    {contract.receipt_volumes.map(
                      ({ point_id: pointId, volume }) => {
                        const point = points[pointId];
                        return (
                          <PointBadge
                            point={point}
                            key={`${contract.id}-receipt-point-${pointId}`}
                            extra_text={
                              volume ? `${numberFormatter(volume, 0)} dth` : ''
                            }
                          />
                        );
                      },
                    )}
                  </td>
                  <td>
                    {contract.delivery_zones?.length > 0 && (
                      <TextDivider>Zones</TextDivider>
                    )}
                    {contract.delivery_zones
                      .map((zoneId) => zones?.[zoneId]?.name)
                      .filter(Boolean)
                      .map((zoneName) => toZoneName(zoneName!))
                      .sort((a, b) =>
                        a.localeCompare(b, undefined, {
                          numeric: true,
                          sensitivity: 'base',
                        }),
                      )
                      .map((zoneName) => (
                        <BadgeDisplay
                          key={`${contract.id}-delivery-zone-${zoneName}`}
                        >
                          {zoneName}
                        </BadgeDisplay>
                      ))}
                    {contract.delivery_volumes.length > 0 && (
                      <TextDivider>Points</TextDivider>
                    )}
                    {contract.delivery_volumes.map(
                      ({ point_id: pointId, volume }) => {
                        const point = points[pointId];
                        return (
                          <PointBadge
                            point={point}
                            key={`${contract.id}-delivery-point-${pointId}`}
                            extra_text={
                              volume ? `${numberFormatter(volume, 0)} dth` : ''
                            }
                          />
                        );
                      },
                    )}
                  </td>
                  <td>{contract.type}</td>
                  <td>{contract.fuel_rate_schedule_name}</td>
                  <td>{contract.commodity_rate_schedule_name}</td>
                  <td>{numberFormatter(contract.max_daily_quantity, 0)}</td>
                  <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                  <td>{new Date(contract.end_date).toLocaleDateString()}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <ReusableModal
        size={'lg'}
        isOpen={Object.keys(rawData).length > 0}
        header={<span className="fs-3">Raw Data</span>}
        onClose={() => setRawData({})}
      >
        <pre>
          <code>{JSON.stringify(rawData, null, 2)}</code>
        </pre>
      </ReusableModal>
    </>
  );
};
